/**
 * googleAuth.js — Firebase Google Sign-In helper for Lite CMS.
 *
 * Uses the Lite-specific Firebase instance (Firebase_lite.js)
 * so it never interferes with the main CMS Firebase app.
 * Exports a single async function `signInWithGoogle` that opens the
 * Google sign-in popup and resolves with the Firebase ID token + basic
 * user profile, or rejects with an error.
 *
 * Usage:
 *   import { signInWithGoogle } from '../lite/googleAuth';
 *   const { idToken, email, displayName, photoURL } = await signInWithGoogle();
 */

import * as firebase from "firebase/app";
import "firebase/auth";
import liteApp from "./Firebase_lite";

/**
 * Opens a Google sign-in popup via the Lite Firebase named app.
 *
 * @returns {Promise<{ idToken: string, email: string, displayName: string, photoURL: string }>}
 */
export async function signInWithGoogle() {
  const GoogleAuthProvider = firebase.default.auth.GoogleAuthProvider;
  const googleProvider = new GoogleAuthProvider();
  googleProvider.addScope("email");
  googleProvider.addScope("profile");

  const auth = liteApp.auth();

  try {
    const result = await auth.signInWithPopup(googleProvider);
    const idToken = await result.user.getIdToken();

    // Guard: if Firebase silently linked this account with Facebook, reject
    const hasFacebookProvider = result.user.providerData.some(
      (p) => p.providerId === "facebook.com",
    );
    if (hasFacebookProvider) {
      await auth.signOut();
      const err = new Error(
        "This email is already registered with Facebook. Please sign in with Facebook instead.",
      );
      err.code = "auth/account-exists-with-different-credential";
      throw err;
    }

    return {
      idToken,
      email: result.user.email,
      displayName: result.user.displayName,
      photoURL: result.user.photoURL,
      social_channel: "google",
      uid: result.user.uid,
    };
  } catch (err) {
    if (err.code === "auth/account-exists-with-different-credential") {
      const methods = await auth
        .fetchSignInMethodsForEmail(err.email)
        .catch(() => []);
      const providerName = methods.includes("facebook.com")
        ? "Facebook"
        : methods[0] || "another provider";
      const friendlyError = new Error(
        `This email is already registered with ${providerName}. Please sign in with ${providerName} instead.`,
      );
      friendlyError.code = "auth/account-exists-with-different-credential";
      friendlyError.linkedProvider = providerName;
      throw friendlyError;
    }
    throw err;
  }
}

/**
 * Opens a Facebook sign-in popup via the Lite Firebase named app.
 *
 * @returns {Promise<{ idToken: string, email: string, displayName: string, photoURL: string }>}
 */
export async function signInWithFacebook() {
  const FacebookAuthProvider = firebase.default.auth.FacebookAuthProvider;
  const facebookProvider = new FacebookAuthProvider();
  facebookProvider.addScope("email");
  facebookProvider.addScope("public_profile");

  const auth = liteApp.auth();

  try {
    const result = await auth.signInWithPopup(facebookProvider);
    console.log("result", result);
    const idToken = await result.user.getIdToken();

    // Guard: if Firebase silently linked this account with Google, reject
    const hasGoogleProvider = result.user.providerData.some(
      (p) => p.providerId === "google.com",
    );
    if (hasGoogleProvider) {
      await auth.signOut();
      const err = new Error(
        "This email is already registered with Google. Please sign in with Google instead.",
      );
      err.code = "auth/account-exists-with-different-credential";
      throw err;
    }

    return {
      idToken,
      email: result.user.email,
      displayName: result.user.displayName,
      photoURL: result.user.photoURL,
      social_channel: "facebook",
      uid: result.user.uid,
    };
  } catch (err) {
    if (err.code === "auth/account-exists-with-different-credential") {
      const methods = await auth
        .fetchSignInMethodsForEmail(err.email)
        .catch(() => []);
      const providerName = methods.includes("google.com")
        ? "Google"
        : methods[0] || "another provider";
      const friendlyError = new Error(
        `This email is already registered with ${providerName}. Please sign in with ${providerName} instead.`,
      );
      friendlyError.code = "auth/account-exists-with-different-credential";
      friendlyError.linkedProvider = providerName;
      throw friendlyError;
    }
    throw err;
  }
}
